import { ErpSpan, ErpDiv } from './ErpHtml';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useDialogFocusRestore } from '@/hooks/useDialogFocusRestore';

export interface ErpOffcanvasProps {
  modelValue?: boolean;
  onUpdateModelValue?: (v: boolean) => void;
  show?: boolean;
  onHide?: () => void;
  onClose?: () => void;
  isOpen?: boolean; // fallback
  toggle?: () => void; // fallback
  title?: string;
  placement?: 'start' | 'end' | 'left' | 'right' | string;
  className?: string;
  right?: boolean;
  shadow?: boolean;
  width?: string | number;
  noHeader?: boolean;
  noCloseOnBackdrop?: boolean;
  noCloseOnEsc?: boolean;
  backdrop?: boolean;
  id?: string;
  bgVariant?: string;

  onHidden?: () => void;
  onShow?: () => void;
  onShown?: () => void;

  headerSlot?: React.ReactNode | ((props: any) => React.ReactNode);
  titleSlot?: React.ReactNode;
  footerSlot?: React.ReactNode | ((props: any) => React.ReactNode);
  [key: string]: any;
}

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';

export const ErpOffcanvas: React.FC<ErpOffcanvasProps> = ({
  modelValue = false,
  isOpen,
  show,
  toggle,
  onUpdateModelValue,
  title,
  right = false,
  placement = right ? 'end' : 'start',
  width,
  noHeader = false,
  noCloseOnBackdrop = false,
  noCloseOnEsc = false,
  className,
  onHide,
  onClose,
  onShow,
  headerSlot,
  titleSlot,
  footerSlot,
  children
}) => {
  const { i18n } = useTranslation();
  const focusRestore = useDialogFocusRestore();
  const visible = isOpen ?? show ?? modelValue;
  const [open, setOpen] = useState(visible);

  useEffect(() => {
    setOpen(visible);
  }, [visible]);

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      onShow?.();
    } else {
      (onHide ?? onClose ?? toggle)?.();
    }
    setOpen(newOpen);
    onUpdateModelValue?.(newOpen);
  };

  const hide = () => handleOpenChange(false);

  const side = placement === 'start' ? (i18n.dir() === 'rtl' ? 'right' : 'left') :
               placement === 'end' ? (i18n.dir() === 'rtl' ? 'left' : 'right') :
               placement === 'left' ? 'left' :
               placement === 'top' ? 'top' : 
               placement === 'bottom' ? 'bottom' : 'right';

  // A sheet anchored to the top/bottom is full width by design; forcing
  // width/max-width there would fight the side classes.
  const horizontalSide = side === 'left' || side === 'right';
  const widthStyle = width && horizontalSide
    ? { maxWidth: typeof width === 'number' ? `${width}px` : width, width: '100%' }
    : undefined;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent {...focusRestore} side={side} style={widthStyle} className={cn('flex max-h-dvh min-w-0 flex-col overflow-hidden p-0', className)}
        onInteractOutside={(event) => { if (noCloseOnBackdrop) event.preventDefault(); }}
        onEscapeKeyDown={(event) => {
          const menuOpen = event.target instanceof Element && event.target.closest('[role="combobox"][aria-expanded="true"]');
          if (noCloseOnEsc || menuOpen) event.preventDefault();
        }}>
        {!noHeader && (
          <SheetHeader className="shrink-0 px-4 pe-12 py-3 border-b text-start">
            {headerSlot ? (
              typeof headerSlot === 'function' ? (headerSlot as any)({ hide }) : headerSlot
            ) : (
              <SheetTitle className="flex min-w-0 flex-wrap justify-between items-center break-words text-lg font-semibold m-0">
                <ErpSpan>{titleSlot || title}</ErpSpan>
              </SheetTitle>
            )}
          </SheetHeader>
        )}

        <ErpDiv className="min-h-0 min-w-0 p-4 flex-1 overflow-y-auto">
          {typeof children === 'function' ? (children as any)({ hide }) : children}
        </ErpDiv>

        {footerSlot && (
          <SheetFooter className="p-4 border-t">
            {typeof footerSlot === 'function' ? (footerSlot as any)({ hide }) : footerSlot}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
};
